import { type Me, displayName, logout } from "@/lib/api";
import { type ThemeMode, useTheme } from "@/lib/theme";
import {
  ArrowUpRight,
  BookOpen,
  ChevronDown,
  LogOut,
  Monitor,
  Moon,
  Sun,
  UserRound,
} from "lucide-react";
import { Popover, RadioGroup } from "radix-ui";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

const themes = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
] as const;

export function AccountAvatar({
  me,
  className = "account-avatar",
}: { me: Me; className?: string }) {
  const [failed, setFailed] = useState(false);
  const name = displayName(me);
  const words = name.split(/\s+/);
  const initials = (
    words.length > 1 ? `${words[0]?.[0]}${words.at(-1)?.[0]}` : name.slice(0, 2)
  ).toUpperCase();
  return (
    <span className={className}>
      {me.avatar_url && !failed ? (
        <img src={me.avatar_url} alt={`${name} avatar`} onError={() => setFailed(true)} />
      ) : (
        <span aria-hidden="true">{initials}</span>
      )}
    </span>
  );
}

export function AccountMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mode, setMode } = useTheme();
  const navigate = useNavigate();
  const name = displayName(me);
  const provider = { github: "GitHub", google: "Google", email: "email" }[me.provider];

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    setError(null);
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch {
      setError("Couldn't sign out. Please try again.");
      setSigningOut(false);
    }
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className="account-trigger" aria-label={`Account menu for ${name}`}>
          <AccountAvatar key={me.avatar_url} me={me} />
          <span className="account-trigger-name">{name}</span>
          <ChevronDown size={14} className="switcher-chevron" aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="identity-popover account-panel"
          align="end"
          sideOffset={10}
          collisionPadding={12}
          aria-label="Your account"
        >
          <div className="account-identity">
            <AccountAvatar key={me.avatar_url} me={me} />
            <div className="identity-copy">
              <strong>{name}</strong>
              <span title={me.email}>{me.email}</span>
            </div>
          </div>
          <p className="account-provider">Signed in with {provider}</p>
          <div className="identity-section">
            <Link to="/settings" className="identity-action" onClick={() => setOpen(false)}>
              <UserRound size={17} aria-hidden="true" />
              <span>
                Account settings<small>Your profile and personal details</small>
              </span>
              <ChevronDown size={14} className="action-forward" aria-hidden="true" />
            </Link>
          </div>
          <div className="appearance-section">
            <p className="identity-label" id="account-appearance">
              Appearance
            </p>
            <RadioGroup.Root
              className="appearance-options"
              value={mode}
              onValueChange={(value) => setMode(value as ThemeMode)}
              orientation="horizontal"
              aria-labelledby="account-appearance"
            >
              {themes.map(({ value, label, icon: Icon }) => (
                <RadioGroup.Item key={value} value={value} className="appearance-option">
                  <Icon size={17} aria-hidden="true" />
                  {label}
                </RadioGroup.Item>
              ))}
            </RadioGroup.Root>
          </div>
          <div className="identity-section">
            <a
              href="https://wardkey.app/docs"
              target="_blank"
              rel="noreferrer"
              className="identity-action"
              onClick={() => setOpen(false)}
            >
              <BookOpen size={17} aria-hidden="true" />
              <span>Documentation</span>
              <ArrowUpRight size={14} aria-hidden="true" />
            </a>
          </div>
          <div className="identity-footer">
            {error && (
              <p className="identity-error" role="alert">
                {error}
              </p>
            )}
            <button
              type="button"
              className="identity-action sign-out"
              disabled={signingOut}
              onClick={handleSignOut}
            >
              <LogOut size={17} aria-hidden="true" />
              <span>{signingOut ? "Signing out…" : "Sign out"}</span>
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
